import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Newspaper, Search, Pin, Clock, Loader2, Eye, Calendar,
  Megaphone, AlertTriangle, Bell, ChevronRight, Sparkles
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const CATEGORIES = [
  { value: 'Announcement', label: 'Announcement' },
  { value: 'Update', label: 'Update' },
  { value: 'Event', label: 'Event' },
  { value: 'Policy Change', label: 'Policy Change' },
  { value: 'Achievement', label: 'Achievement' },
  { value: 'Reminder', label: 'Reminder' },
  { value: 'Other', label: 'Other' },
];

const PRIORITY_STYLES = {
  normal: { bg: 'bg-gray-50', border: 'border-gray-200', badge: 'bg-gray-100 text-gray-700' },
  important: { bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-800' },
  urgent: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-800' },
};

const CATEGORY_ICONS = {
  'Announcement': Megaphone,
  'Update': Sparkles,
  'Event': Calendar,
  'Policy Change': Newspaper,
  'Achievement': Sparkles,
  'Reminder': Bell,
  'Other': Newspaper,
};

const Newsroom = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPost, setSelectedPost] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);

  const { getAuthHeaders, currentOffice } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPosts();
  }, [currentOffice]);

  const fetchPosts = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }

      const response = await fetch(`${API_URL}/api/news/`, { headers });
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const openPostModal = async (post) => {
    // Fetch full post details (increments view count)
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }
      
      const response = await fetch(`${API_URL}/api/news/${post.id}`, { headers });
      if (response.ok) {
        const fullPost = await response.json();
        setSelectedPost(fullPost);
        setShowPostModal(true);
      }
    } catch (error) {
      console.error('Error fetching post details:', error);
      setSelectedPost(post);
      setShowPostModal(true);
    }
  };

  // Filter posts
  const filteredPosts = posts.filter(post => {
    if (searchQuery && !post.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (selectedCategory !== 'all' && post.category !== selectedCategory) return false;
    return true;
  });

  // Separate pinned and regular posts
  const pinnedPosts = filteredPosts.filter(p => p.is_pinned);
  const regularPosts = filteredPosts.filter(p => !p.is_pinned);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Megaphone className="h-7 w-7 text-blue-600" />
          Newsroom
        </h1>
        <p className="text-gray-600">Stay updated with the latest announcements and news</p>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search news..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-news-input"
                />
              </div>
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px]" data-testid="filter-category-select">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Pinned Posts */}
      {pinnedPosts.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Pin className="h-5 w-5 text-purple-600" />
            Pinned
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {pinnedPosts.map(post => {
              const styles = PRIORITY_STYLES[post.priority] || PRIORITY_STYLES.normal;
              const IconComponent = CATEGORY_ICONS[post.category] || Newspaper;
              
              return (
                <Card 
                  key={post.id}
                  className={`${styles.bg} border-2 ${styles.border} hover:shadow-lg transition-all cursor-pointer`}
                  onClick={() => openPostModal(post)}
                  data-testid={`pinned-post-${post.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${post.priority === 'urgent' ? 'bg-red-100' : 'bg-purple-100'}`}>
                        <Pin className={`h-6 w-6 ${post.priority === 'urgent' ? 'text-red-600' : 'text-purple-600'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-semibold text-lg">{post.title}</h3>
                          {post.is_new && (
                            <Badge className="bg-blue-100 text-blue-800 text-xs">New</Badge>
                          )}
                          {post.priority === 'urgent' && (
                            <Badge className="bg-red-100 text-red-800 text-xs">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Urgent
                            </Badge>
                          )}
                        </div>
                        <p className="text-gray-600 text-sm line-clamp-2 mb-2">
                          {post.summary || post.content.substring(0, 120)}...
                        </p>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <IconComponent className="h-3 w-3" />
                            {post.category}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(post.published_at)}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Regular Posts */}
      {regularPosts.length > 0 ? (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Recent Updates</h2>
          <div className="space-y-3">
            {regularPosts.map(post => {
              const styles = PRIORITY_STYLES[post.priority] || PRIORITY_STYLES.normal;
              const IconComponent = CATEGORY_ICONS[post.category] || Newspaper;
              
              return (
                <Card 
                  key={post.id}
                  className={`${styles.bg} border ${styles.border} hover:shadow-md transition-all cursor-pointer`}
                  onClick={() => openPostModal(post)}
                  data-testid={`news-post-${post.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg bg-white border ${styles.border}`}>
                        <IconComponent className="h-5 w-5 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium">{post.title}</h3>
                          {post.is_new && (
                            <Badge className="bg-blue-100 text-blue-800 text-xs">New</Badge>
                          )}
                          {post.priority === 'important' && (
                            <Badge className={styles.badge + ' text-xs'}>Important</Badge>
                          )}
                          {post.priority === 'urgent' && (
                            <Badge className={styles.badge + ' text-xs'}>
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Urgent
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 line-clamp-1">
                          {post.summary || post.content.substring(0, 100)}...
                        </p>
                      </div>
                      <div className="text-right text-xs text-gray-400 whitespace-nowrap">
                        <div>{formatDate(post.published_at)}</div>
                        <div className="flex items-center justify-end gap-1 mt-1">
                          <Eye className="h-3 w-3" />
                          {post.view_count}
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ) : filteredPosts.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Newspaper className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No news yet</h3>
            <p className="text-gray-500 mt-1">
              {searchQuery || selectedCategory !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'Check back later for updates'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Post Detail Modal */}
      <Dialog open={showPostModal} onOpenChange={setShowPostModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="news-detail-modal">
          {selectedPost && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-3">
                  {selectedPost.is_pinned && (
                    <Pin className="h-6 w-6 text-purple-600 flex-shrink-0 mt-1" />
                  )}
                  <div>
                    <DialogTitle className="text-xl">{selectedPost.title}</DialogTitle>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge className={PRIORITY_STYLES[selectedPost.priority]?.badge || 'bg-gray-100'}>
                        {selectedPost.priority}
                      </Badge>
                      <Badge variant="outline">{selectedPost.category}</Badge>
                      {selectedPost.is_new && (
                        <Badge className="bg-blue-100 text-blue-800">New</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4">
                {/* Meta info */}
                <div className="flex flex-wrap gap-4 text-sm text-gray-500 pb-4 border-b">
                  {selectedPost.published_at && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {new Date(selectedPost.published_at).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Eye className="h-4 w-4" />
                    {selectedPost.view_count} views
                  </span>
                  {selectedPost.created_by_name && (
                    <span>Posted by {selectedPost.created_by_name}</span>
                  )}
                </div>

                {/* Urgent warning */}
                {selectedPost.priority === 'urgent' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-800">Urgent Notice</p>
                      <p className="text-sm text-red-600">This is a high-priority announcement that requires your immediate attention.</p>
                    </div>
                  </div>
                )}

                {/* Content */}
                <div className="prose max-w-none">
                  <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                    {selectedPost.content}
                  </div>
                </div>

                {/* Expiry notice */}
                {selectedPost.expires_at && (
                  <div className="text-sm text-gray-500 pt-4 border-t">
                    <Clock className="h-4 w-4 inline mr-1" />
                    This post expires on {new Date(selectedPost.expires_at).toLocaleDateString()}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowPostModal(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Newsroom;
